import { ColumnsType } from "antd/es/table";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useThresholdData } from "src/core/hooks/useThresholdData";
import { formatNumber } from "src/core/utils/format";
import { getGroupedCellRowSpan } from "src/core/utils/table";
import { getThresholdClass } from "src/core/utils/threshold";
import {
  ContentWithHoverTotals,
  PropertyToSum,
} from "src/modules/ContentWithHoverTotals/ContentWithHoverTotals";
import { ValueWithCurrency } from "src/modules/ValueWithCurrency/ValueWithCurrency";
import { ValueWithThreshold } from "src/modules/ValueWithThreshold/ValueWithThreshold";
import { ThresholdData } from "src/types/common";
import { RecapSectionDataItem } from "src/types/priceConfigurator";

export function useRoutingExplosionRecapColumns(
  dataItems: RecapSectionDataItem[],
  byMainLayer: boolean,
  hoverTotalsContentKey?: string,
  thresholdData: ThresholdData[] = []
) {
  const { t } = useTranslation();

  const { dlThresholdValue, pureTimeThresholdValue } = useThresholdData(
    thresholdData,
    dataItems[0]?.currencyCode
  );

  const getTableColumns = useCallback(() => {
    const hoverPropertiesToSum: PropertyToSum[] = [
      {
        property: "dl",
        label: t("tableColumn.dl"),
      },
      {
        property: "pureTime",
        label: t("tableColumn.pureTime"),
      },
    ];

    let columns: ColumnsType<RecapSectionDataItem> = [
      {
        title: t("tableColumn.father"),
        key: "fatherCodeDescription_column",
        align: "center",
        width: 120,
        render: (item: RecapSectionDataItem, record, index) => {
          return (item.rowType ?? null) !== null ? (
            item.costTypeMapping
          ) : hoverTotalsContentKey ? (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={["costTypeMapping", "fatherCodeDescription"]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.fatherCodeDescription}
            </ContentWithHoverTotals>
          ) : (
            item.fatherCodeDescription
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
          ]),
        }),
      },
      {
        title: t("tableColumn.fatherCode"),
        key: "fatherCode_column",
        align: "center",
        width: 80,
        render: (item: RecapSectionDataItem, record, index) => {
          return hoverTotalsContentKey ? (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={[
                "costTypeMapping",
                "fatherCodeDescription",
                "fatherCode",
              ]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.fatherCode}
            </ContentWithHoverTotals>
          ) : (
            item.fatherCode
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
            "fatherCode",
          ]),
        }),
      },
      {
        title: t("tableColumn.componentDescriptionShort"),
        key: "componentDescription_column",
        width: 90,
        align: "center",
        render: (item: RecapSectionDataItem, record, index) => {
          return hoverTotalsContentKey ? (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={[
                "costTypeMapping",
                "fatherCodeDescription",
                "fatherCode",
                "componentDescription",
              ]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.componentDescription}
            </ContentWithHoverTotals>
          ) : (
            item.componentDescription
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
            "fatherCode",
            "componentDescription",
          ]),
        }),
      },
      {
        title: t("tableColumn.component"),
        key: "component_column",
        width: 90,
        align: "center",
        render: (item: RecapSectionDataItem, record, index) => {
          return hoverTotalsContentKey ? (
            <ContentWithHoverTotals
              contentKey={hoverTotalsContentKey}
              currentDataItem={item}
              dataItems={dataItems}
              filterProperties={[
                "costTypeMapping",
                "fatherCodeDescription",
                "fatherCode",
                "componentDescription",
                "component",
              ]}
              propertiesToSum={hoverPropertiesToSum}
              hoverCalculationsDisabled={(item.rowType ?? null) !== null}
              useCurrentPriceConfiguratorData={true}
            >
              {item.component}
            </ContentWithHoverTotals>
          ) : (
            item.component
          );
        },
        onCell: (record: RecapSectionDataItem, index: number | undefined) => ({
          rowSpan: getGroupedCellRowSpan(index ?? 0, record, dataItems, [
            "costTypeMapping",
            "fatherCodeDescription",
            "fatherCode",
            "componentDescription",
            "component",
          ]),
        }),
      },
      {
        title: t("tableColumn.from"),
        key: "from_column",
        align: "center",
        width: 120,
        render: (item: RecapSectionDataItem, record, index) => {
          return item.from;
        },
      },
      {
        title: t("tableColumn.mainLayer"),
        key: "mainLayer_column",
        align: "center",
        width: 150,
        render: (item: RecapSectionDataItem, record, index) => {
          return item.mainLayer;
        },
      },
    ];

    if (byMainLayer) {
      columns.push({
        title: t("tableColumn.dl"),
        key: "dl_column",
        align: "center",
        className: "bold",
        width: 80,
        render: (item: RecapSectionDataItem) => {
          return (
            <ValueWithCurrency
              value={item.dl}
              currencyCode={item.currencyCode}
              className={getThresholdClass(item.dl, dlThresholdValue)}
            />
          );
        },
      });

      columns.push({
        title: t("tableColumn.sc"),
        key: "sc_column",
        align: "center",
        className: "bold",
        width: 80,
        render: (item: RecapSectionDataItem) => {
          return (
            <ValueWithCurrency
              value={item.sc}
              currencyCode={item.currencyCode}
            />
          );
        },
      });

      columns.push({
        title: t("tableColumn.pureTime"),
        key: "pureTime_column",
        width: 110,
        align: "center",
        render: (item: RecapSectionDataItem) => {
          return (
            <ValueWithThreshold
              value={item.pureTime}
              thresholdValue={pureTimeThresholdValue}
              disabled={item.id < 0}
            >
              {formatNumber(item.pureTime)}
            </ValueWithThreshold>
          );
        },
      });
    } else {
      columns.push({
        title: t("tableColumn.singleLayer"),
        key: "singleLayer_column",
        align: "center",
        width: 200,
        render: (item: RecapSectionDataItem) => {
          return item.singleLayer;
        },
      });

      columns.push({
        title: t("tableColumn.operationCodeDescription"),
        key: "operationCodeDescription_column",
        width: 280,
        align: "center",
        render: (item: RecapSectionDataItem) => {
          return item.operationCodeDescription;
        },
      });

      columns.push({
        title: t("tableColumn.dl"),
        key: "dl_column",
        align: "center",
        className: "bold",
        width: 80,
        render: (item: RecapSectionDataItem) => {
          return (
            <ValueWithCurrency
              value={item.dl}
              currencyCode={item.currencyCode}
              className={getThresholdClass(
                item.dl,
                dlThresholdValue,
                undefined,
                item.id < 0
              )}
            />
          );
        },
      });

      columns.push({
        title: t("tableColumn.sc"),
        key: "sc_column",
        align: "center",
        className: "bold",
        width: 80,
        render: (item: RecapSectionDataItem) => {
          return (
            <ValueWithCurrency
              value={item.sc}
              currencyCode={item.currencyCode}
            />
          );
        },
      });

      columns.push({
        title: t("tableColumn.pureTime"),
        key: "pureTime_column",
        align: "center",
        width: 60,
        render: (item: RecapSectionDataItem) => {
          return (
            <ValueWithThreshold
              value={item.pureTime}
              thresholdValue={pureTimeThresholdValue}
              disabled={item.id < 0}
            >
              {formatNumber(item.pureTime)}
            </ValueWithThreshold>
          );
        },
      });
    }

    return columns;
  }, [
    byMainLayer,
    dataItems,
    dlThresholdValue,
    hoverTotalsContentKey,
    pureTimeThresholdValue,
    t,
  ]);

  return {
    getTableColumns: getTableColumns,
  };
}
